import React, { useEffect, useRef, useState } from "react";

// Normalizes whatever shape the geography search endpoints return into {code, name}.
const normalizeOptions = (raw, codeKeys, nameKeys) => {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const seen = new Set();
  return list
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") return { code: item, name: item };
      const code = codeKeys.map((k) => item[k]).find(Boolean) || item.name;
      const name = nameKeys.map((k) => item[k]).find(Boolean) || code;
      return { code, name };
    })
    .filter(Boolean)
    .filter((o) => {
      const key = `${(o.name || "").toLowerCase()}::${(o.code || "").toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

/**
 * Typeahead field for Country/State/City — shared by any form that collects these (Add Office,
 * Add Warehouse, Create Location). Falls back to free text if nothing is selected from the
 * dropdown, matching CreateLocationTab.js's original per-screen implementation (the first place
 * this pattern was built); extracted here so a third form doesn't hand-roll it again. Pass
 * onPersistNew to also save a genuinely new value server-side when nothing matches (used for
 * city, whose reference list is user-growable, unlike the fixed country/state lists).
 */
export default function GeoTypeaheadField({
  label,
  required = false,
  disabled = false,
  error,
  nameValue, // current display name (e.g. "United States")
  onSelect, // (opt: {code, name}) => void -- called on pick, and on blur/enter with free text
  searchFn, // (query) => Promise<axios response>; response.data is normalized via codeKeys/nameKeys
  codeKeys = ["code"],
  nameKeys = ["name"],
  minQueryLength = 2,
  onPersistNew, // optional: (typedName) => Promise<{code,name}|void>
  placeholder,
}) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  const runSearch = async (q) => {
    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      const resp = await searchFn(q, controller.signal);
      const opts = normalizeOptions(resp.data, codeKeys, nameKeys);
      setOptions(opts);
      setOpen(true);
      setActiveIdx(opts.length ? 0 : -1);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setOptions([]);
      setOpen(true);
      setActiveIdx(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disabled) { setOptions([]); setOpen(false); return undefined; }
    const q = (query || nameValue || "").trim();
    if (q.length < minQueryLength) { setOptions([]); setOpen(false); return undefined; }
    const id = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, disabled]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleSelect = (opt) => {
    onSelect(opt);
    setQuery("");
    setOpen(false);
    setActiveIdx(-1);
  };

  const persistTypedIfNeeded = async (typedName) => {
    const trimmed = (typedName || "").trim();
    if (!trimmed) return;
    const exact = options.find((o) => (o.name || "").toLowerCase() === trimmed.toLowerCase());
    if (exact) { handleSelect(exact); return; }
    if (onPersistNew) {
      try {
        const saved = await onPersistNew(trimmed);
        if (saved) { handleSelect(saved); return; }
      } catch {
        // fall through to plain free-text fallback below
      }
    }
    // Free-text fallback -- whatever was typed becomes both the code and the display name.
    handleSelect({ code: trimmed, name: trimmed });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <span className="text-dense-xs text-gray-500 dark:text-acs-dark-subtext">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      <div
        className={`relative flex items-center w-full px-fluid-2 py-fluid-1.5 mt-fluid-0.25
          bg-[#FAFAFB] dark:bg-acs-dark-surface text-gray-700 dark:text-acs-dark-text
          rounded-md border ${error ? "border-red-500 ring-1 ring-red-200 dark:ring-red-900/40" : "border-gray-300 dark:border-acs-dark-border"}
          text-fluid-sm font-inter
          focus-within:ring-2 focus-within:ring-blue-400 dark:focus-within:ring-[#223688]
          focus-within:border-blue-400 dark:focus-within:border-[#223688]
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <input
          type="text"
          autoComplete="off"
          disabled={disabled}
          value={query || nameValue || ""}
          onFocus={() => setOpen(options.length > 0)}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (activeIdx >= 0) { e.preventDefault(); handleSelect(options[activeIdx]); }
              else if (query.trim()) { e.preventDefault(); persistTypedIfNeeded(query); }
            } else if (!open) return;
            else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(options.length - 1, i + 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
            else if (e.key === "Escape" || e.key === "Tab") setOpen(false);
          }}
          onBlur={() => {
            setTimeout(() => {
              if (query.trim()) persistTypedIfNeeded(query);
              setOpen(false);
            }, 200);
          }}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none dark:text-acs-dark-text"
        />
      </div>
      <ErrorText message={error} />
      {open && (loading || options.length > 0 || (onPersistNew && query.trim().length > 0)) && (
        <ul role="listbox" className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface shadow-lg">
          {loading && <li className="px-3 py-2 text-sm text-gray-500 dark:text-acs-dark-subtext">Searching…</li>}
          {!loading && options.map((opt, idx) => (
            <li
              key={`${opt.code}-${idx}`}
              role="option"
              aria-selected={idx === activeIdx}
              className={`px-3 py-2 cursor-pointer text-sm ${idx === activeIdx ? "bg-[#e8edff] dark:bg-acs-dark-border text-[#223688] dark:text-acs-dark-text" : "hover:bg-gray-50 dark:hover:bg-acs-dark-border dark:text-acs-dark-text"}`}
              onMouseEnter={() => setActiveIdx(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(opt)}
            >
              {opt.name}{opt.code && opt.code !== opt.name ? ` (${opt.code})` : ""}
            </li>
          ))}
          {!loading && onPersistNew && options.length === 0 && query.trim().length > 0 && (
            <li className="px-3 py-2 text-sm text-gray-500 dark:text-acs-dark-subtext">
              No match — "{query.trim()}" will be added as a new {label.toLowerCase()}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function ErrorText({ message }) {
  if (!message) return null;
  return <p className="text-fluid-xs text-red-600 mt-fluid-0.5">{message}</p>;
}
